import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import { useQueryString } from 'src/app/shared/hooks/location';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';
import { getQueryStringSingleValue } from 'src/app/shared/utils/query';

import { prefetchPsfSharePageDataFromApi, usePsfSharePageDataFromApi } from './pageData';

export type PsfSharePagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof usePsfSharePageDataFromApi>[0]>;
}>;

export type PsfSharePageCombinedState = MergedState<ReturnType<typeof usePsfSharePageDataFromApi>>;

export function usePageContext(
	slug: string,
	prefetchedData?: PsfSharePagePrefetchedContext,
): PsfSharePageCombinedState {
	const query = useQueryString();
	return useMergedStates(
		usePsfSharePageDataFromApi(prefetchedData?.pageData, slug, {
			queryParams: {
				share: getQueryStringSingleValue(query, 'share'),
				skip: getQueryStringSingleValue(query, 'skip'),
			},
			banditOverrides: {
				copy: getQueryStringSingleValue(query, 'bo.copy'),
			},
		}),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PsfSharePagePrefetchedContext> {
	const [pageData] = await Promise.all([
		prefetchPsfSharePageDataFromApi(context, slug, {
			queryParams: {
				share: getQueryStringSingleValue(context.query, 'share'),
				skip: getQueryStringSingleValue(context.query, 'skip'),
			},
			banditOverrides: {
				copy: getQueryStringSingleValue(context.query, 'bo.copy'),
			},
		}),
	]);

	return { pageData };
}

export type PsfSharePagePrefetchedDataProps = PrefetchedDataProps<PsfSharePagePrefetchedContext>;
